import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripe } from '../components/TricolorStripe';
import { ProductCard } from '../components/ProductCard';

export default function CataloguePage() {
  const { t, p } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialSport = searchParams.get('sport') || 'all';
  const [activeFilter, setActiveFilter] = useState(initialSport);

  useEffect(() => {
    const sport = searchParams.get('sport');
    if (sport) {
      setActiveFilter(sport);
    }
  }, [searchParams]);

  const filters = [
    { key: 'all', label: t.catalogue.filters.all },
    { key: 'accessories', label: t.catalogue.filters.accessories },
    { key: 'balls', label: t.catalogue.filters.balls },
    { key: 'goalkeeper', label: t.catalogue.filters.goalkeeper },
    { key: 'shorts', label: t.catalogue.filters.shorts },
    { key: 'soccer', label: t.catalogue.filters.soccer },
    { key: 'training', label: t.catalogue.filters.training },
    { key: 'referee', label: t.catalogue.filters.referee },
  ];

  const filterTagMap = {
    soccer: 'Soccer',
    goalkeeper: 'Goalkeeper',
    basketball: 'Basketball',
    referee: ['Referee', 'Arbitre'],
    training: ['Training', 'Entraînement'],
    accessories: ['Accessories', 'Accessoires'],
    balls: ['Balls', 'Ballons'],
    shorts: ['Shorts', 'Short'],
  };

  // New / updated products surface first; untouched legacy products move down.
  const featuredOrder = [
    'milano', 'celtic', 'malta', 'manchester', 'capri', 'london-m900', 'model-i', 'team-polo', 'defender-short', 'buffon', 'gk-halftone',
    'rimini', 'team-shorts', 'base-layer', 'kit-bag', 'shinguards', 'winter-jacket', 'light-jacket', 'padded-vest',
    'roma-m090', 'goalie', 'model-h', 'model-e', 'mondial-m8070', 'rio-pc600', 'san-marino', 'lugano', 'sorento-m906', 'verona-m907',
    'catania-m908', 'training-jacket', 'neptune', 'porto-k015',
  ];
  const rank = (prod) => {
    const idx = prod.slug ? featuredOrder.indexOf(prod.slug) : -1;
    if (idx !== -1) return idx;
    return prod.slug ? 500 : 1000;
  };

  const base = activeFilter === 'all'
    ? p
    : p.filter((product) => {
        const filterTags = filterTagMap[activeFilter];
        const wanted = Array.isArray(filterTags) ? filterTags : [filterTags];
        const productTags = product.tags || [product.tag];
        return productTags.some((pt) => wanted.includes(pt));
      });

  const filteredProducts = [...base].sort((a, b) => rank(a) - rank(b));

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="catalogue-page">
      {/* Hero */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920"
            alt="Catalogue"
            className="w-full h-full object-cover grayscale"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative z-10 text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-5xl md:text-6xl lg:text-7xl text-white tracking-tight"
          >
            {t.catalogue.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-dm text-base text-white/80 mt-4 max-w-2xl mx-auto"
          >
            {t.catalogue.subtitle}
          </motion.p>
        </div>
      </section>

      <TricolorStripe />

      {/* Filter Bar */}
      <section className="sticky top-20 z-30 bg-[#0D0D0D]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 -mb-2 scrollbar-hide">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => {
                  setActiveFilter(filter.key);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`font-dm text-sm tracking-wider px-4 py-2 whitespace-nowrap transition-colors duration-200 ${
                  activeFilter === filter.key
                    ? 'bg-[#CE2B37] text-white'
                    : 'bg-transparent text-white/60 hover:text-white'
                }`}
                data-testid={`filter-${filter.key}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredProducts.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <p className="font-dm text-white/60">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      <TricolorStripe />

      {/* CTA Banner */}
      <section className="py-24 md:py-32 bg-[#0D0D0D]" data-testid="catalogue-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-bebas text-4xl md:text-5xl text-white tracking-tight"
          >
            {t.catalogue.ctaBanner.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-dm text-base text-white/60 mt-4 max-w-xl mx-auto"
          >
            {t.catalogue.ctaBanner.body}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <Link
              to="/contact"
              className="inline-block bg-[#CE2B37] text-white font-bebas text-lg tracking-wider px-10 py-4 mt-8 hover:bg-[#a82230] transition-colors duration-200"
              data-testid="catalogue-contact-cta"
            >
              {t.catalogue.ctaBanner.cta}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
